import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { FiUser, FiMapPin, FiTruck, FiStar, FiNavigation, FiX } from 'react-icons/fi';
import { MoverIcon } from './utils/mapConfig';
import { getTruckTypeLabel, formatTND, formatMoverRating, formatDistanceKm } from './utils/helpers';

function FitBounds({ coords, results }) {
  const map = useMap();
  useEffect(() => {
    if (!coords) return;
    const points = [L.latLng(coords.lat, coords.lng)];
    results.forEach(r => {
      if (r.position?.lat && r.position?.lng) {
        points.push(L.latLng(r.position.lat, r.position.lng));
      }
    });
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [80, 80] });
    } else if (points.length === 1) {
      map.setView(points[0], 13);
    }
  }, [coords, results, map]);
  return null;
}

// Custom Popup Component
const CustomPopup = ({ mover, onClose }) => (
  <div className="min-w-[250px] p-0">
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg overflow-hidden">
      {/* Header avec gradient */}
      <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 p-4 border-b border-amber-500/30">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center">
              <FiUser className="w-5 h-5 text-gray-900" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">
                {mover.firstName} {mover.lastName}
              </h3>
              <div className="flex items-center gap-1 text-xs text-gray-300">
                <FiStar className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                <span>{formatMoverRating(mover.rating, mover.reviewCount ?? mover.reviews)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-300">
          <FiMapPin className="w-4 h-4 text-green-400" />
          <span>{formatDistanceKm(mover.distance)} km de distance</span>
        </div>
        {mover.truckType && (
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <FiTruck className="w-4 h-4 text-amber-400" />
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded-full border border-amber-500/30">
              {getTruckTypeLabel(mover.truckType)}
            </span>
          </div>
        )}
        {mover.price && (
          <div className="pt-2 border-t border-gray-700">
            <div className="text-lg font-bold text-amber-400">
              {formatTND(mover.price)}
            </div>
            <div className="text-xs text-gray-400">Estimation</div>
          </div>
        )}
      </div>
    </div>
  </div>
);

// User Location Marker avec animation
const UserLocationMarker = ({ position }) => {
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const userIcon = L.divIcon({
    html: `
      <div class="relative">
        ${isAnimating ? `
          <div class="absolute inset-0 animate-ping">
            <div class="w-12 h-12 bg-blue-400 rounded-full opacity-75"></div>
          </div>
        ` : ''}
        <div class="relative w-10 h-10 bg-blue-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
          <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
          </svg>
        </div>
      </div>
    `,
    className: "custom-user-marker",
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });

  return (
    <Marker position={position} icon={userIcon}>
      <Popup>
        <div className="font-bold text-blue-400">📍 Votre position</div>
      </Popup>
    </Marker>
  );
};

const MapView = ({ coords, results, radiusKm, geoError, onSelectMover }) => {
  const [selectedMarker, setSelectedMarker] = useState(null);

  if (!coords) {
    return (
      <div className="h-[600px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center border border-gray-700 shadow-2xl relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-amber-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center animate-pulse">
            <FiMapPin className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-400">{geoError || "En attente de votre position..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[600px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden border border-gray-700 shadow-2xl relative">
      {/* Légende */}
      <div className="absolute top-4 left-4 z-[1000] bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 border border-gray-700 shadow-lg">
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
            <span className="text-gray-300">Votre position</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-400 rounded-full border-2 border-white"></div>
            <span className="text-gray-300">Déménageurs ({results.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 border-2 border-amber-500 rounded-full"></div>
            <span className="text-gray-300">Rayon: {radiusKm} km</span>
          </div>
        </div>
      </div>

      {/* Info badge résultats */}
      {results.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-4 right-4 z-[1000] bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 px-4 py-2 rounded-lg shadow-lg font-semibold text-sm"
        >
          {results.length} déménageur{results.length > 1 ? 's' : ''} trouvé{results.length > 1 ? 's' : ''}
        </motion.div>
      )}

      <MapContainer
        center={[coords.lat, coords.lng]}
        zoom={12}
        scrollWheelZoom={true}
        zoomControl={false}
        style={{ height: "100%", width: "100%", zIndex: 0 }}
        className="map-container"
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />

        {/* User location */}
        <UserLocationMarker position={[coords.lat, coords.lng]} />

        {/* Rayon de recherche avec animation */}
        <Circle
          center={[coords.lat, coords.lng]}
          radius={radiusKm * 1000}
          pathOptions={{
            color: "#F59E0B",
            fillColor: "#F59E0B",
            fillOpacity: 0.15,
            weight: 2,
            dashArray: "10, 5"
          }}
        />

        {/* Markers des déménageurs avec animation */}
        {results.map((r) => {
          if (!r.position?.lat || !r.position?.lng) return null;
          
          return (
            <Marker
              key={r.id}
              position={[r.position.lat, r.position.lng]}
              icon={MoverIcon}
              eventHandlers={{
                click: () => {
                  setSelectedMarker(r.id);
                  if (onSelectMover) onSelectMover(r);
                },
              }}
            >
              <Popup
                className="custom-popup"
                closeButton={false}
                autoClose={false}
                closeOnClick={false}
              >
                <CustomPopup mover={r} onClose={() => setSelectedMarker(null)} />
              </Popup>
            </Marker>
          );
        })}

        <FitBounds coords={coords} results={results} />
        <ZoomControl position="bottomright" />
      </MapContainer>

      {/* Styles personnalisés pour la carte */}
      <style>{`
        .map-container {
          filter: brightness(0.95) contrast(1.1);
        }
        .custom-popup .leaflet-popup-content-wrapper {
          background: transparent;
          box-shadow: none;
          padding: 0;
        }
        .custom-popup .leaflet-popup-content {
          margin: 0;
        }
        .custom-popup .leaflet-popup-tip {
          background: #1f2937;
          border: 1px solid #374151;
        }
        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3) !important;
        }
        .leaflet-control-zoom a {
          background: rgba(31, 41, 55, 0.9) !important;
          backdrop-filter: blur(8px);
          color: #fbbf24 !important;
          border: 1px solid #374151 !important;
        }
        .leaflet-control-zoom a:hover {
          background: rgba(245, 158, 11, 0.2) !important;
        }
      `}</style>
    </div>
  );
};

export default MapView;




